import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Shadow.css';
import ParameterSearchModal from './ParameterSearchModal';

const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

interface DeviceData {
  id: string;
  msgNumber: number;
  extra: string;
  eid: number;
  eventTime: string;
  tz: string;
  positionTime: string;
  lat: number;
  long: number;
  ign: number;
  eng: number;
  spdk: number;
  head: number;
  alt: number;
  odo: number;
  lac: number;
  cid: number;
  css: number;
  vin: string;
  vbat: number;
  fix: number;
  sat: number;
  satu: number;
  tvi: number;
  nxt: number;
}

interface DeviceInfo {
  fleet: string;
  device: string;
  driver: string;
  licensePlate: string;
  lastMessage: string;
  status: 'Connected' | 'Disconnected';
}

interface Vehicle {
  id: number;
  vehicleId: string;
  fleet: string;
  licensePlate: string;
  driverId: string;
  name: string;
  make: string;
  unitId: string;
  firmware: string;
  eFuel: string;
  eSafe: string;
  hardware: string;
  lastTransmission: string;
  online: boolean;
  selected: boolean;
}

interface ShadowProps {
  initialUnitId?: string;
}

const Shadow: React.FC<ShadowProps> = ({ initialUnitId }) => {
  const [view, setView] = useState<'cmd' | 'vehicles'>('cmd');
  const [unitId, setUnitId] = useState(initialUnitId || '');
  const [command, setCommand] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [deviceData, setDeviceData] = useState<DeviceData[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<number[]>([]);
  const [filterText, setFilterText] = useState('');
  const [mapLocation, setMapLocation] = useState<{lat: number, long: number} | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [commandSuccess, setCommandSuccess] = useState(false);
  const [isParameterModalOpen, setIsParameterModalOpen] = useState(false);

  // Update unitId when initialUnitId prop changes
  useEffect(() => {
    if (initialUnitId) {
      setUnitId(initialUnitId);
      setView('cmd'); // Make sure we're on CMD view
    }
  }, [initialUnitId]);

  const toggleRowExpansion = (idx: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedRows(newExpanded);
  };

  const convertToNZTime = (utcTime: string) => {
    if (!utcTime) return '';
    try {
      const date = new Date(utcTime);
      return date.toLocaleString('en-NZ', { 
        timeZone: 'Pacific/Auckland',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return utcTime;
    }
  };

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    if (view === 'vehicles') {
      fetchVehicles();
    }
  }, [view]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await axios.get(`${API_BASE}/api/shadow/vehicles`, {
        signal: controller.signal,
        headers: getHeaders()
      });
      
      clearTimeout(timeoutId);
      setVehicles(response.data.vehicles || []);
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Vehicles fetch timed out');
      } else {
        console.error('Error fetching vehicles:', error);
      }
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleVehicleSelection = (id: number) => {
    setSelectedVehicles(prev => 
      prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]
    );
  };

  const toggleAllVehicles = () => {
    if (selectedVehicles.length === vehicles.length) {
      setSelectedVehicles([]);
    } else {
      setSelectedVehicles(vehicles.map(v => v.id));
    }
  };

  const handleSearch = async () => {
    if (!unitId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/shadow/device/${unitId}/events`, { headers: getHeaders() });
      setDeviceData(response.data || []);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching device:', error);
      setDeviceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendCommand = async () => {
    if (!unitId || !command) return;
    setLoading(true);
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await axios.post(`${API_BASE}/api/shadow/send-command`, { 
        imeis: [parseInt(unitId)], 
        command 
      }, {
        signal: controller.signal,
        headers: getHeaders()
      });
      
      clearTimeout(timeoutId);
      setCommandSuccess(true);
      setTimeout(() => setCommandSuccess(false), 5000);
      // setCommand cleared only by Clear button;
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        alert('Command timed out. The server is taking too long to respond.');
      } else {
        console.error('Error sending command:', error);
        alert('Failed to send command');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearchByDates = async () => {
    if (!unitId || !startDate || !endDate) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/shadow/device/${unitId}/history`, {
        params: { startDate, endDate },
        headers: getHeaders()
      });
      setDeviceData(response.data);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setUnitId('');
    // setCommand cleared only by Clear button;
    setStartDate('');
    setEndDate('');
    setDeviceInfo(null);
    setDeviceData([]);
    setCurrentPage(1);
  };

  const handleRefresh = async () => {
    if (!unitId || loading) return;
    setLoading(true);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await axios.get(`${API_BASE}/api/shadow/device/${unitId}/events`, {
        signal: controller.signal,
        headers: getHeaders()
      });
      
      clearTimeout(timeoutId);
      
      if (response.data && Array.isArray(response.data)) {
        setDeviceData(response.data);
      }
      
      setCurrentPage(1);
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        console.log('Refresh timed out');
      } else {
        console.error('Error refreshing:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const csv = [
      Object.keys(deviceData[0] || {}).join(','),
      ...deviceData.map(row => Object.values(row).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `device_${unitId}_${Date.now()}.csv`;
    a.click();
  };

  const handleLocationClick = (lat: number, long: number) => {
    setMapLocation({ lat, long });
  };

  return (
    <div className="shadow-container">
      <div style={{
        background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
        color: 'white',
        padding: '15px 20px',
        borderRadius: '8px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        boxShadow: '0 4px 15px rgba(251, 191, 36, 0.3)'
      }}>
        <span style={{ fontSize: '24px' }}>🌑</span>
        <div>
          <div style={{ fontSize: '18px', fontWeight: '700' }}>SHADOW Server</div>
          <div style={{ fontSize: '12px', opacity: 0.9 }}>MyAsset Platform - Shadow Devices</div>
        </div>
      </div>
      <div className="view-toggle">
        <button 
          className={view === 'cmd' ? 'active' : ''} 
          onClick={() => setView('cmd')}
        >
          CMD
        </button>
        <button 
          className={view === 'vehicles' ? 'active' : ''} 
          onClick={() => setView('vehicles')}
        >
          Vehicles
        </button>
      </div>

      {view === 'cmd' ? (
        <>
          <div className="shadow-search-section">
            <div className="search-grid">
              <div className="search-group">
                <label>Unit ID</label>
                <div className="input-wrapper">
                  <input
                    type="text"
                    value={unitId}
                    onChange={(e) => setUnitId(e.target.value)}
                    placeholder="007A99"
                  />
                  <button className="btn-search" onClick={handleSearch}>
                    🔍 Search
                  </button>
                </div>
              </div>

              <div className="search-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label>Send Command</label>
                  <button className="btn-clear-small" onClick={handleClear}>
                    🗑️ Clear
                  </button>
                </div>
                <div className="input-wrapper">
                  <input
                    type="text"
                    value={command}
                    onChange={(e) => setCommand(e.target.value)}
                    placeholder="Enter command"
                  />
                  <button className="btn-send" onClick={handleSendCommand}>
                    📤 Send
                  </button>
                  <button 
                    className="btn-search-params" 
                    onClick={() => setIsParameterModalOpen(true)}
                  >
                    🔍 Parameters
                  </button>
                  {commandSuccess && <span className="success-check">✓</span>}
                </div>
              </div>

              <div className="search-group date-range">
                <label>Date Range</label>
                <div className="date-inputs">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    placeholder="Start"
                  />
                  <span className="date-separator">→</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    placeholder="End"
                  />
                  <button className="btn-search" onClick={handleSearchByDates}>
                    📅 Search
                  </button>
                </div>
              </div>
            </div>
          </div>

          {deviceInfo && (
            <div className="device-info">
              <span>Fleet: {deviceInfo.fleet}</span>
              <span>Device: {deviceInfo.device}</span>
              <span>Driver: {deviceInfo.driver}</span>
              <span>License plate: {deviceInfo.licensePlate}</span>
              <span>Last message: {deviceInfo.lastMessage}</span>
              <span className={`status ${deviceInfo.status.toLowerCase()}`}>{deviceInfo.status}</span>
            </div>
          )}

          <div className="shadow-table-section">
            <div className="table-actions">
              <input type="text" placeholder="Filter" className="filter-input" />
              <button className="btn-export" onClick={handleExport} disabled={deviceData.length === 0}>📄 Export</button>
              <button className="btn-refresh" onClick={handleRefresh} disabled={!unitId || loading}>
                {loading ? '⟳ Refreshing...' : '🔄 Refresh'}
              </button>
            </div>

            <div className="table-wrapper">
              <table className="shadow-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>ID</th>
                    <th>Msg Number</th>
                    <th>Extra</th>
                    <th>EID</th>
                    <th>Event Time (NZ)</th>
                    <th>TZ</th>
                    <th>Position Time (NZ)</th>
                    <th>LAT</th>
                    <th>LONG</th>
                    <th>IGN</th>
                    <th>ENG</th>
                    <th>SPDK</th>
                    <th>HEAD</th>
                    <th>ALT</th>
                    <th>ODO</th>
                    <th>LAC</th>
                    <th>CID</th>
                    <th>CSS</th>
                    <th>VIN</th>
                    <th>VBAT</th>
                    <th>FIX</th>
                    <th>SAT</th>
                    <th>SATU</th>
                    <th>TVI</th>
                    <th>NXT</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceData.length === 0 ? (
                    <tr>
                      <td colSpan={27} style={{ textAlign: 'center', padding: '40px' }}>
                        {loading ? 'Loading...' : 'No data available. Search for a device to view data.'}
                      </td>
                    </tr>
                  ) : (
                    deviceData.map((row, idx) => (
                      <tr key={idx} className={expandedRows.has(idx) ? 'expanded-row' : ''}>
                        <td className="expand-cell">
                          <button 
                            className="expand-btn"
                            onClick={() => toggleRowExpansion(idx)}
                            title={expandedRows.has(idx) ? 'Collapse' : 'Expand details'}
                          >
                            {expandedRows.has(idx) ? '−' : '+'}
                          </button>
                        </td>
                        <td className="id-cell">{row.id}</td>
                        <td>{row.msgNumber}</td>
                        <td className="extra-cell">
                          {expandedRows.has(idx) ? (
                            <div className="extra-expanded">{row.extra || '—'}</div>
                          ) : (
                            <div className="extra-truncated" title={row.extra}>
                              {row.extra ? (row.extra.length > 15 ? row.extra.substring(0, 15) + '...' : row.extra) : '—'}
                            </div>
                          )}
                        </td>
                        <td className="eid-cell">{row.eid}</td>
                        <td className="time-cell">{convertToNZTime(row.eventTime)}</td>
                        <td>{row.tz}</td>
                        <td className="time-cell">{convertToNZTime(row.positionTime)}</td>
                        <td className="coord-cell" onClick={() => handleLocationClick(row.lat, row.long)} style={{cursor: 'pointer', color: '#0066cc'}}>{row.lat}</td>
                        <td className="coord-cell" onClick={() => handleLocationClick(row.lat, row.long)} style={{cursor: 'pointer', color: '#0066cc'}}>{row.long}</td>
                        <td className={`status-cell ${row.ign === 1 ? 'status-on' : 'status-off'}`}>
                          {row.ign === 1 ? '●' : '○'}
                        </td>
                        <td className={`status-cell ${row.eng === 1 ? 'status-on' : 'status-off'}`}>
                          {row.eng === 1 ? '●' : '○'}
                        </td>
                        <td className="speed-cell">{row.spdk}</td>
                        <td>{row.head}</td>
                        <td>{row.alt}</td>
                        <td className="odo-cell">{row.odo}</td>
                        <td>{row.lac}</td>
                        <td>{row.cid}</td>
                        <td>{row.css}</td>
                        <td className="voltage-cell">{row.vin}</td>
                        <td className="voltage-cell">{row.vbat}</td>
                        <td>{row.fix}</td>
                        <td className="sat-cell">{row.sat}</td>
                        <td>{row.satu}</td>
                        <td className="temp-cell">{row.tvi}</td>
                        <td>{row.nxt}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {deviceData.length > 0 && (
              <div className="pagination">
                <button disabled={currentPage === 1}>‹</button>
                <button className="active">1</button>
                <button>2</button>
                <button>3</button>
                <button>4</button>
                <button>5</button>
                <button>›</button>
                <button>››</button>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="vehicles-section">
          <div className="vehicles-header">
            <input 
              type="text" 
              placeholder="🔍 All fields filter" 
              className="filter-input-large"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
            />
            <span className="selection-count">
              {selectedVehicles.length} vehicles selected of {vehicles.length}
            </span>
            <button className="btn-export" onClick={handleExport}>📄 Export</button>
          </div>

          <div className="table-wrapper">
            <table className="vehicles-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>
                    <input 
                      type="checkbox" 
                      checked={selectedVehicles.length === vehicles.length && vehicles.length > 0}
                      onChange={toggleAllVehicles}
                    />
                  </th>
                  <th>Vehicle ID</th>
                  <th>Fleet</th>
                  <th>License Plate</th>
                  <th>Driver ID</th>
                  <th>Name</th>
                  <th>Make</th>
                  <th>UnitID</th>
                  <th>Firmware</th>
                  <th>eFuel</th>
                  <th>eSafe</th>
                  <th>Hardware</th>
                  <th>Last Transmission</th>
                  <th>Online</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={15} style={{ textAlign: 'center', padding: '40px' }}>
                      Loading vehicles...
                    </td>
                  </tr>
                ) : vehicles.length === 0 ? (
                  <tr>
                    <td colSpan={15} style={{ textAlign: 'center', padding: '40px' }}>
                      No vehicles found
                    </td>
                  </tr>
                ) : (
                  vehicles.map((vehicle, idx) => (
                    <tr 
                      key={vehicle.id}
                      className={`${selectedVehicles.includes(vehicle.id) ? 'selected-row' : ''} ${vehicle.online ? 'online-row' : 'offline-row'}`}
                      onClick={async (e) => {
                        if ((e.target as HTMLElement).tagName !== 'INPUT') {
                          setView('cmd');
                          setUnitId(vehicle.unitId);
                          setLoading(true);
                          try {
                            const response = await axios.get(`${API_BASE}/api/shadow/device/${vehicle.unitId}`, { headers: getHeaders() });
                            setDeviceInfo(response.data.info);
                            setDeviceData(response.data.data);
                            setCurrentPage(1);
                          } catch (error) {
                            console.error('Error fetching device:', error);
                          } finally {
                            setLoading(false);
                          }
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{idx + 1}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <input 
                          type="checkbox" 
                          checked={selectedVehicles.includes(vehicle.id)}
                          onChange={() => toggleVehicleSelection(vehicle.id)}
                        />
                      </td>
                      <td>{vehicle.vehicleId}</td>
                      <td>{vehicle.fleet}</td>
                      <td>{vehicle.licensePlate}</td>
                      <td>{vehicle.driverId}</td>
                      <td>{vehicle.name}</td>
                      <td>{vehicle.make}</td>
                      <td>{vehicle.unitId}</td>
                      <td>{vehicle.firmware}</td>
                      <td>{vehicle.eFuel}</td>
                      <td>{vehicle.eSafe}</td>
                      <td>{vehicle.hardware}</td>
                      <td>{vehicle.lastTransmission}</td>
                      <td>
                        <span className={`online-indicator ${vehicle.online ? 'online' : 'offline'}`}>
                          ●
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button disabled={currentPage === 1}>‹‹</button>
            <button disabled={currentPage === 1}>‹</button>
            <button className="active">1</button>
            <button>2</button>
            <button>›</button>
            <button>››</button>
          </div>
        </div>
      )}

      {mapLocation && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setMapLocation(null)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              maxWidth: '90vw',
              maxHeight: '90vh'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h3 style={{ margin: 0 }}>Location: {mapLocation.lat.toFixed(6)}, {mapLocation.long.toFixed(6)}</h3>
              <button 
                onClick={() => setMapLocation(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  color: '#666'
                }}
              >×</button>
            </div>
            <iframe
              width="800"
              height="600"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyAJTKR898aljDfpXT_PuXIFPDnrwHA7EAw&q=${mapLocation.lat},${mapLocation.long}&zoom=16`}
            />
          </div>
        </div>
      )}
      
      <ParameterSearchModal
        isOpen={isParameterModalOpen}
        onClose={() => setIsParameterModalOpen(false)}
        onSelectCommand={(cmd) => setCommand(cmd)}
      />
    </div>
  );
};

export default Shadow;
